// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

import { imageFormatFor } from "@/lib/agentcore-client"
import { copy } from "@/lib/copy"

/**
 * Phase label shown while a turn with attachments starts. Names the TYPE and the
 * COUNT of what was attached (ADR-DOC-06): with attachments a turn runs 3–5 min and
 * a bare "Pensando…" never says the wait is because of the file.
 *
 * | attached                        | label                     |
 * |---------------------------------|---------------------------|
 * | 1 image                         | Analizando imagen…        |
 * | 2+ images                       | Analizando imágenes…      |
 * | 1 document, nothing else        | Analizando documento…     |
 * | 1 document + 1 image            | Analizando documentos…    |
 * | 2+ documents (+ any images)     | Analizando documentos…    |
 *
 * Documents win any mix — they are the attachment the user expects to see *read* —
 * and the plural follows the TOTAL FILE COUNT, not the document count. With a PDF and
 * a JPG attached the user sees two chips and is waiting on two files, so the singular
 * would read as if one of them had been ignored. That is the whole point of this task:
 * never imply an attachment was dropped.
 *
 * (I first implemented the plural by document count, which contradicted the spec. The
 * mutation test then "caught" the correct rule as a defect, because the test encoded
 * my misreading. Mutation testing verifies that a rule is enforced, never that the
 * rule is the right one.)
 *
 * WHY THIS LIVES IN ITS OWN MODULE
 * --------------------------------
 * It started inside `ChatInterface.tsx`, so the test had to reproduce the rule
 * instead of importing it — which verified nothing: flipping the rule in the
 * component left all 29 tests green. Extracted here so the tests call the real
 * function.
 *
 * Takes `{ name, type }` rather than `File` so tests need no DOM File instances;
 * `File` structurally satisfies it.
 */
export function initialStatusLabel(files: readonly { name?: string; type?: string }[]): string {
  const hasDoc = files.some(f => imageFormatFor(f) === undefined)
  const plural = files.length > 1
  if (hasDoc) {
    return plural ? copy.status.analyzingDocuments : copy.status.analyzingDocument
  }
  return plural ? copy.status.analyzingImages : copy.status.analyzingImage
}

/**
 * Label for the second phase of a document turn: the bytes are in S3 and the runtime
 * is now reading them.
 *
 * Plural follows the same rule as {@link initialStatusLabel} — the screenshot that
 * exposed this showed "Procesando documento…" (singular) with a PDF *and* a JPG
 * attached, so the count has to agree there too.
 *
 * Images have no second phase: they ride in the request body, so there is nothing new
 * to announce and they keep the "Analizando…" label until the first byte.
 */
export function processingStatusLabel(files: readonly { name?: string; type?: string }[]): string {
  return files.length > 1 ? copy.status.processingDocuments : copy.status.processingDocument
}
